/**
 * Ask a question in plain language.
 *
 * The model translates; it does not answer. What comes back is a *query* —
 * scope, filters, free text — which the caller then runs through the ordinary
 * search endpoints, so grants and scoping are applied by exactly the same code
 * as a hand-built filter. Nothing here is a shortcut around authorisation.
 *
 * Two things are shown deliberately. **What it understood**, in the model's own
 * words, because a query the user cannot read is a query they cannot correct.
 * And the fact that only the metadata schema — container, table and column
 * names, plus sample values from fields not classified as personal data — is
 * ever sent. Documents and rows are not.
 *
 * When no model is reachable the panel says so once and hands the question to
 * text search rather than failing. An archive that stops working because an AI
 * service is down is not an archive.
 */

import { Alert, Badge, Button, Group, Paper, Stack, Text, TextInput } from "@mantine/core";
import { IconArrowRight, IconInfoCircle, IconSparkles } from "@tabler/icons-react";
import { useState } from "react";

import { useAccessToken } from "@/features/auth/useAccessToken";
import { archiveNlQuery } from "@/shared/api/archive";

/** The shape `archive_nl_query.InterpretedQuery.to_dict()` produces. Typed here
 *  rather than in the generated client because the endpoint returns it as a
 *  free-form object — the model's output is validated server-side, and pinning
 *  it in the OpenAPI schema would imply a stability the contract does not have. */
export type Interpreted = {
  kind?: string;
  container_ids?: string[];
  dataset_id?: string | null;
  record_view_id?: string | null;
  filters?: { column: string; op: string; value: string }[];
  free_text?: string;
  explanation?: string;
};

export function ArchiveAskPanel({
  containerId,
  onApply,
  onBrowseRecords,
}: {
  containerId: string | null;
  /** Applied to the document search: free text, scope, and any filters that
   *  match a facet the current scope actually offers. */
  onApply: (interpreted: Interpreted) => void;
  /** Records live in a retired system's tables, not in document search. When
   *  the model resolves a question to those, we route rather than pretend. */
  onBrowseRecords: (containerId: string) => void;
}) {
  const getToken = useAccessToken();
  const [question, setQuestion] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<Interpreted | null>(null);
  const [unavailable, setUnavailable] = useState<string | null>(null);

  async function ask() {
    const asked = question.trim();
    if (!asked) return;
    setBusy(true);
    try {
      const token = await getToken();
      const out = await archiveNlQuery(token, asked, containerId ? [containerId] : []);
      const interpreted = out.interpreted as Interpreted;
      if (!out.available) {
        setUnavailable(out.fallback_reason || "No model is configured.");
        setResult(null);
        // Still useful: hand the question to text search rather than stopping.
        onApply({ free_text: asked });
        return;
      }
      setUnavailable(null);
      setResult(interpreted);
      if (interpreted.kind === "records") return; // routed by the button below
      onApply(interpreted);
    } catch (err) {
      setUnavailable(err instanceof Error ? err.message : String(err));
      setResult(null);
      onApply({ free_text: asked });
    } finally {
      setBusy(false);
    }
  }

  const recordsTarget = result?.kind === "records" ? (result.container_ids?.[0] ?? null) : null;

  return (
    <Paper withBorder p="md" radius="md">
      <Group gap="xs" mb="xs">
        <IconSparkles size={16} />
        <Text size="sm" fw={600}>
          Ask a question
        </Text>
        <Text size="xs" c="dimmed">
          in plain language
        </Text>
      </Group>

      <Group gap="sm" wrap="nowrap">
        <TextInput
          flex={1}
          value={question}
          placeholder="e.g. serious cases reported from Germany, or temperature excursions in Mainz last year"
          onChange={(event) => setQuestion(event.currentTarget.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter") void ask();
          }}
        />
        <Button loading={busy} disabled={!question.trim()} onClick={() => void ask()}>
          Ask
        </Button>
      </Group>

      {result?.explanation && (
        <Alert mt="sm" variant="light" color="teal" icon={<IconInfoCircle size={18} />}>
          <Text size="sm">{result.explanation}</Text>
          {!!result.filters?.length && (
            <Group gap={6} mt={8}>
              {result.filters.map((f, i) => (
                <Badge key={`${f.column}-${i}`} size="sm" variant="light" tt="none">
                  {f.column} {f.op} {f.value}
                </Badge>
              ))}
            </Group>
          )}
          {recordsTarget && (
            <Button
              mt="sm"
              size="xs"
              variant="light"
              rightSection={<IconArrowRight size={14} />}
              onClick={() => onBrowseRecords(recordsTarget)}
            >
              Open these records
            </Button>
          )}
          {result.kind === "records" && !recordsTarget && (
            <Text size="xs" c="dimmed" mt={6}>
              That question is about a retired system&apos;s tables. Open the archive and use Browse
              tables.
            </Text>
          )}
        </Alert>
      )}

      {unavailable && (
        <Alert mt="sm" variant="light" color="gray" icon={<IconInfoCircle size={18} />}>
          <Text size="sm">
            No model answered, so the question was passed to text search instead.
          </Text>
          <Text size="xs" c="dimmed" mt={4}>
            {unavailable}
          </Text>
        </Alert>
      )}

      <Stack gap={2} mt="sm">
        <Text size="xs" c="dimmed">
          Only the metadata schema — archive, table and column names — is sent to the model.
          Documents and rows are not.
        </Text>
        <Text size="xs" c="dimmed">
          The model builds the query; it does not answer it. Results come from the archive, with
          your access applied.
        </Text>
      </Stack>
    </Paper>
  );
}
